import {IntegrationTask} from '@app/views/admin/application-settings/models/integration-task.model';
import {
    OrgItem,
    OrgItemChanges,
    OrgItemType,
    OUPathParser
} from '@app/views/admin/application-settings/models/orgItem.model';
import {
    DataAttribute,
    GroupIdPropertyNames,
    SchoolIdPropertyNames,
    SectionIdPropertyNames
} from '@app/views/admin/application-settings/models/data-attribute.model';
import {
    INTEGRATION_TASKS_EDUTONE_TITLE_MAP,
    INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP,
    INTEGRATION_TASKS_ORDER
} from '@app/views/admin/application-settings/models/integration.model';
import {NEW_ORG_IDENTIFIER} from '@app/views/admin/application-settings/models/organizationsMapping.model';
import {Argument} from '@app/core/model/filtering/argument';
import {EntityFilterModel, FilterAction} from '@app/core/model/filtering/entity-filter-model';
import {FilterPredicate} from '@app/core/model/filtering/filter-predicate';
import {PropertyFilterModel} from '@app/core/model/filtering/property-filter-model';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {FiltersListService} from '@app/views/admin/application-settings/components/filters/filters-list-wrapper/filters-list.service';
import {ApplicationSettingsService} from '@app/views/admin/application-settings/application-settings.service';
import {OUTreeNodeType} from '@app/views/admin/application-settings/models/ou-tree-node-type.model';

export enum ViewTreeNodeType {
    SCHOOL = 'school',
    ENTITY_TYPE = 'entity type',
    GROUP = 'group',
    SECTION = 'section',
    VALUE = 'value',
    OU_TREE = 'ou tree',
    RAW_RULES = 'raw rules'
}

export class MappingRulesViewConfig {
    constructor(
        public title: string = '',
        public nodeTypes: ViewTreeNodeType[] = []
    ) { }
}

export abstract class MappingRulesGroupsCreator {
    constructor(public orgUnitPathAttributeName: string) {
    }

    protected doesAllMappingRulesExistInOUChildren(currentItem: ViewTreeNodeItem): boolean {
        return currentItem.mappingRules.every((mappingRule: MappingRule) =>
            currentItem.children
            && currentItem.children
                .filter((node: ViewTreeNodeItem) =>
                    node.titles.every((titleItem: MappingRulesTitleItemValue) => titleItem.type === OUTreeNodeType.OU)
                )
                .some((child: ViewTreeNodeItem) =>
                    child.mappingRules.find((item: MappingRule) => item.filterIndex === mappingRule.filterIndex) !== undefined
                )
        );
    }

    protected doesAllOUChildrenHasMappingRules(currentItem: ViewTreeNodeItem): boolean {
        return currentItem.children
            .filter((node: ViewTreeNodeItem) =>
                node.titles.every((titleItem: MappingRulesTitleItemValue) => titleItem.type === OUTreeNodeType.OU)
            )
            .every((child: ViewTreeNodeItem) =>
                child.mappingRules.length > 0 && this.doesAllOUChildrenHasMappingRules(child)
            );
    }
}


export class MappingRulesOUPathGroupsCreator extends MappingRulesGroupsCreator {
    constructor(orgUnitPathAttributeName: string,
                public pathParser: OUPathParser,
                public sourceOrgTypeMap: Map<string, OrgItemType> = null) {
        super(orgUnitPathAttributeName);
    }

    public getMappingRulesGroups(currentItem: ViewTreeNodeItem): MappingRulesGroup[] {
        let result: MappingRulesGroup[] = [];

        const leafMappingRules = currentItem.mappingRules
            .filter((mappingRule: ViewMappingRule) => !mappingRule.isIntermediateNode);

        // Don't search mapping rules groups if node is a leaf for some mapping rules
        if (leafMappingRules.length > 0) {
            result = leafMappingRules.map((mappingRule: MappingRule) => {
                const mappingRuleContainerPathList = [];
                const mappingRuleOUPathList = [].concat(
                    ...this.getMappingRulesOUPathList([mappingRule], [this.orgUnitPathAttributeName])
                ).filter((path: string) => {
                    if (this.sourceOrgTypeMap.get(path) === OrgItemType.CONTAINER) {
                        mappingRuleContainerPathList.push(path);
                        return false;
                    }
                    // (this.sourceOrgTypeMap.get(path) === OrgItemType.OU)
                    return true;
                });
                const mappingRuleGroupPathList = mappingRuleContainerPathList.concat(
                    ...this.getMappingRulesOUPathList([mappingRule], [GROUP_MEMBERSHIP])
                );

                return new MappingRulesGroup(
                    this.getEqualOUPathGroup(mappingRuleOUPathList, mappingRuleGroupPathList),
                    false,
                    [mappingRule]
                );
            });
        } else {
            // Search equal OU Path parts only for intermediate nodes

            // Search equal OU Path parts of OrgUnitPath in filter rules
            const equalOUPathPartList: string[] = this
                .getEqualOUPathPartList(currentItem.mappingRules, [this.orgUnitPathAttributeName], [OrgItemType.OU]);

            // Search equal OU Path parts of GroupsMemberships in filter rules
            const equalGroupsPathPartList: string[] = this
                .getEqualOUPathPartList(currentItem.mappingRules,
                    [this.orgUnitPathAttributeName, GROUP_MEMBERSHIP], [OrgItemType.CONTAINER, OrgItemType.GROUP], true);

            const equalPathPartList = this.getEqualOUPathGroup(equalOUPathPartList, equalGroupsPathPartList);

            if (equalPathPartList.length > 0) {
                result.push(new MappingRulesGroup(equalPathPartList, true, currentItem.mappingRules));
            }
        }

        return result;
    }

    public getArgumentsList(pathList: string[]): Argument[] {
        return pathList.map((path: string) => new Argument(path, path));
    }

    public getEqualOUPathGroup(equalOUPathPartList: string[], equalGroupsPathPartList: string[]): MappingRulesTitleItemValue[] {
        return this.getMappingRulesTitles(this.getArgumentsList(equalOUPathPartList), this.getArgumentsList(equalGroupsPathPartList));
    }

    public getMappingRulesTitles(equalOUPathPartList: Argument[], equalGroupsPathPartList: Argument[]): MappingRulesTitleItemValue[] {
        const result: MappingRulesTitleItemValue[] = [];

        equalOUPathPartList.forEach((arg: Argument) => {
            const orgName = OrgItem.getOrgName(arg.id, arg.name, OrgItemType.OU, this.pathParser.dataSourceType);
            result.push(
                new MappingRulesTitleItemValue(
                    new Argument(arg.id, orgName),
                    <string>OUTreeNodeType.OU,
                    this.sourceOrgTypeMap.has(arg.id) ? this.sourceOrgTypeMap.get(arg.id) : OUTreeNodeType.OU
                )
            );
        });

        equalGroupsPathPartList.forEach((arg: Argument) => {
            const orgName = OrgItem.getOrgName(arg.id, arg.name, OrgItemType.GROUP, this.pathParser.dataSourceType);
            result.push(
                new MappingRulesTitleItemValue(
                    new Argument(arg.id, orgName),
                    <string>OUTreeNodeType.GROUP,
                    this.sourceOrgTypeMap.has(arg.id) ? this.sourceOrgTypeMap.get(arg.id) : OUTreeNodeType.GROUP
                )
            );
        });

        return result;
    }

    private getEqualOUPathPartList(mappingRules: MappingRule[],
        orgUnitPathPropertyNames: string[],
        allowedOrgItemTypes: OrgItemType[] = [],
        fullMatch = false): string[] {
        const mappingRulesOUPathList = this.getMappingRulesOUPathList(mappingRules, orgUnitPathPropertyNames)
            .map((mappingRuleOUPathList: string[]) =>
                mappingRuleOUPathList.filter((path: string) => allowedOrgItemTypes.indexOf(this.sourceOrgTypeMap.get(path)) > -1)
            );

        if (mappingRulesOUPathList.some((mappingRuleOUPathList: string[]) => mappingRuleOUPathList.length === 0)) {
            return [];
        }

        if (fullMatch) {
            // Search full match of OU paths
            if (mappingRulesOUPathList.length > 0) {
                mappingRulesOUPathList.sort((listA: string[], listB: string[]) => listA.length - listB.length);
                return mappingRulesOUPathList[0].filter((ouPath: string) =>
                    mappingRulesOUPathList.every((ouPathList: string[]) => ouPathList.indexOf(ouPath) > -1)
                );
            }

            return [];
        } else {
            // Search equal part of OU path parts
            const splittedMappingRulesOUPathList =
                [].concat(...mappingRulesOUPathList)
                    .map((ouPath: string) => this.pathParser.getNodeList(ouPath))
                    .sort((listA: string[], listB: string[]) => listA.length - listB.length);

            if (splittedMappingRulesOUPathList.length === 0) {
                return [];
            } else {
                let equalOUPathPart = splittedMappingRulesOUPathList[0];
                splittedMappingRulesOUPathList.forEach((splitedList: string[]) => {
                    let i = 0;
                    while ((equalOUPathPart.length > i) && (equalOUPathPart[i] === splitedList[i++])) {
                    }
                    if (equalOUPathPart[i - 1] !== splitedList[i - 1]) {
                        equalOUPathPart = equalOUPathPart.slice(0, i - 1);
                    }
                });
                if ((equalOUPathPart.length === 1) && (equalOUPathPart[0] === '')) {
                    return [];
                } else {
                    return [this.pathParser.getPath(equalOUPathPart)];
                }
            }
        }
    }

    public getMappingRulesOUPathList(mappingRules: MappingRule[], orgUnitPathPropertyNames: string[]): string[][] {
        return mappingRules
            .map((mappingRule: MappingRule) =>
                [].concat(
                    ...mappingRule.propertyFilters
                        .filter((propertyFilter: PropertyFilterModel) =>
                            orgUnitPathPropertyNames.indexOf(propertyFilter.propertyName) > -1
                        )
                        .map((propertyFilter: PropertyFilterModel) => propertyFilter.arguments)
                        .map((args: Argument[]) =>
                            args.map((arg: Argument) => arg.id)
                        )
                )
            );
    }

    public getMappingRulesByPathPart(mappingRules: MappingRule[], pathPart: string): MappingRule[] {
        return !pathPart
            ? mappingRules
            : mappingRules.filter((mappingRule: MappingRule) =>
                mappingRule.propertyFilters
                    .find((propertyFilter: PropertyFilterModel) =>
                        ([this.orgUnitPathAttributeName, GROUP_MEMBERSHIP].indexOf(propertyFilter.propertyName) > -1)
                        && this.doesOUPropertyFilterMatchPathPart(propertyFilter.arguments, pathPart)
                    )
            );
    }

    public isMappingRuleContainsPathPart(mappingRule: MappingRule, pathPart: string, propertyNames: string[]): boolean {
        return (mappingRule.propertyFilters
            .find((propertyFilter: PropertyFilterModel) => {
                const typesOfMatchedPropertyFilterPathPart = this.getTypesOfMatchedPropertyFilterPathPart(propertyFilter.arguments, pathPart);
                return (propertyNames.indexOf(propertyFilter.propertyName) > -1)
                &&
                (
                    (
                        propertyFilter.propertyName === this.orgUnitPathAttributeName && typesOfMatchedPropertyFilterPathPart.includes(OrgItemType.OU)
                    )
                    ||
                    (
                        propertyFilter.propertyName === this.orgUnitPathAttributeName
                        && typesOfMatchedPropertyFilterPathPart.includes(OrgItemType.CONTAINER)
                        && (!!propertyFilter.arguments.find((arg: Argument) => arg.id === pathPart))
                    )
                    ||
                    (
                        propertyFilter.propertyName === GROUP_MEMBERSHIP
                            && !!propertyFilter.arguments.find((arg: Argument) => arg.id === pathPart)
                    )
                );
            })) !== undefined;
    }

    public doesOUPropertyFilterMatchPathPart(args: Argument[], pathPart: string): boolean {
        return !!args.find((arg: Argument) =>
            this.pathParser.isFirstPathPartMatched(arg.id, pathPart)
        );
    }

    public getTypesOfMatchedPropertyFilterPathPart(args: Argument[], pathPart: string): OrgItemType[] {
        const matchedPathParts = args.filter((arg: Argument) =>
            this.pathParser.isFirstPathPartMatched(arg.id, pathPart)
        );

        let isOU, isContainer;
        const result: OrgItemType[] = <OrgItemType[]>[];
        if (matchedPathParts.length > 0) {
            isOU = matchedPathParts.find((arg: Argument) =>
                this.sourceOrgTypeMap.get(arg.id) === OrgItemType.OU
            );
            if (isOU) {
                result.push(OrgItemType.OU);
            }

            isContainer = matchedPathParts.find((arg: Argument) =>
                this.sourceOrgTypeMap.get(arg.id) === OrgItemType.CONTAINER
            );
            if (isContainer) {
                result.push(OrgItemType.CONTAINER);
            }
        }
        return result;
    }

    public getMappingRuleFilterValues(mappingRule: MappingRule, propertyNames: string[]): Argument[] {
        return [].concat(
            ...mappingRule.propertyFilters
                .filter((propertyFilter: PropertyFilterModel) =>
                    (propertyNames.indexOf(propertyFilter.propertyName) > -1) && (propertyFilter.arguments.length > 0)
                )
                .map((propertyFilter: PropertyFilterModel) => propertyFilter.arguments)
        );
    }
}


export class MappingRulesAssignedValuesGroupsCreator extends MappingRulesGroupsCreator {
    public getMappingRulesGroups(currentItem: ViewTreeNodeItem, tasks: IntegrationTask[] = [], eduToneDS = ''): MappingRulesGroup[] {
        const result: MappingRulesGroup[] = [];

        const doesAllMappingRulesExistInChildren = this.doesAllMappingRulesExistInOUChildren(currentItem);
        const isLeafNode = !doesAllMappingRulesExistInChildren;
        // Do not create mapping rules groups for intermediate (parent) nodes - [axof:8117]
        const createMappingRulesGroupsForIntermediateNode = false && this.doesAllOUChildrenHasMappingRules(currentItem);

        // Create Mapping Rules Group only if all children has at least one mapping rule or it is "the leaf for mapping rules"
        if (createMappingRulesGroupsForIntermediateNode || isLeafNode) {
            if (isLeafNode) {
                // Is "the leaf for mapping rules"
                // Create mapping rules group for every mapping rule
                currentItem.mappingRules
                    .map((mappingRule: ViewMappingRule) =>
                        this
                            .getEqualAssignedValuesMappingRulesGroup([mappingRule], currentItem, tasks, eduToneDS)
                    )
                    .filter((mappingRulesGroup: MappingRulesGroup) => mappingRulesGroup)
                    .forEach((mappingRulesGroup: MappingRulesGroup) =>
                        result.push(mappingRulesGroup)
                    );
            } else {
                // Is intermediate node
                // Search equal Assigned values (custom values or task Id) in filter rules
                const mappingRulesGroup: MappingRulesGroup = this
                    .getEqualAssignedValuesMappingRulesGroup(currentItem.mappingRules, currentItem, tasks, eduToneDS);

                if (mappingRulesGroup) {
                    result.push(mappingRulesGroup);
                }
            }
        }

        return result;
    }

    private getEqualAssignedValuesMappingRulesGroup(
        mappingRules: ViewMappingRule[],
        currentItem: ViewTreeNodeItem,
        tasks: IntegrationTask[], eduToneDS: string
    ): MappingRulesGroup {

        const equalAssignedValues = this.getEqualAssignedValues(mappingRules);

        if (equalAssignedValues.size > 0) {
            const taskType = equalAssignedValues.get(CUSTOM_VALUE_TASK_PARAM_NAME);
            const attributes = this.getTaskEduToneAttributes(taskType, tasks, eduToneDS);

            let mappingRulesGroup = this.getMappingRulesGroupValues(equalAssignedValues, attributes);

            mappingRulesGroup = this.setMappingRulesGroupInherited(mappingRulesGroup, currentItem);

            const doesAllMappingRulesExistInChildren = this.doesAllMappingRulesExistInOUChildren(currentItem);

            return new MappingRulesGroup(mappingRulesGroup, doesAllMappingRulesExistInChildren, mappingRules);
        }

        return null;
    }

    private getEqualAssignedValues(mappingRules: ViewMappingRule[]): Map<string, Argument> {
        let equalAssignedValues: Map<string, Argument> = null;
        if (mappingRules.length > 0 && mappingRules[0].customValues) {
            equalAssignedValues = new Map<string, Argument>(mappingRules[0].customValues.entries());
            equalAssignedValues.set(
                CUSTOM_VALUE_TASK_PARAM_NAME, new Argument(mappingRules[0].taskId, mappingRules[0].entityType));
        } else {
            equalAssignedValues = new Map<string, Argument>();
        }

        // Search Equal Custom Values list of node item mapping rules
        return mappingRules
            .reduce((assignedValues: Map<string, Argument>, mappingRule: MappingRule) => {
                assignedValues.forEach((value: Argument, paramName: string) => {
                    if ((paramName !== CUSTOM_VALUE_TASK_PARAM_NAME)
                        && (!mappingRule.customValues.has(paramName)
                            || ((mappingRule.customValues.get(paramName) && value
                                && !this.isEqualArguments(mappingRule.customValues.get(paramName), value, paramName))))) {
                        assignedValues.delete(paramName);
                    }
                });

                const taskId = assignedValues.get(CUSTOM_VALUE_TASK_PARAM_NAME);
                if (taskId && (taskId.id !== mappingRule.taskId)) {
                    assignedValues.delete(CUSTOM_VALUE_TASK_PARAM_NAME);
                }

                return assignedValues;
            }, equalAssignedValues);
    }

    private isEqualArguments(arg1: Argument, arg2: Argument, paramName: string): boolean {
        const isNewSchool = (paramName === SCHOOL_ID && arg1.id === NEW_ORG_IDENTIFIER);
        return isNewSchool ? arg1.name === arg2.name : arg1.id === arg2.id;
    }

    private setMappingRulesGroupInherited(
        mappingRulesGroup: MappingRulesTitleItemValue[], currentItem: ViewTreeNodeItem
    ): MappingRulesTitleItemValue[] {

        if ((currentItem.titles[0].type === OUTreeNodeType.OU) && currentItem.parent) {
            // Loop through parent mapping rules group to find the same equal assigned values and delete it
            currentItem.parent.mappingRulesGroups
                // .filter((parentMappingRulesGroup: MappingRulesGroup) => parentMappingRulesGroup.title !== '')
                .forEach((parentMappingRulesGroup: MappingRulesGroup) => {
                    parentMappingRulesGroup.values
                        .forEach((parentMappingRuleGroupValue: MappingRulesTitleItemValue) => {
                            const mappingRulesGroupValue = mappingRulesGroup
                                .find((item: MappingRulesTitleItemValue) =>
                                    (item.title && (item.title === parentMappingRuleGroupValue.title))
                                    || (!item.title && (item.type === parentMappingRuleGroupValue.type))
                                );
                            if ((mappingRulesGroupValue !== undefined)
                                && (mappingRulesGroupValue.value
                                    && parentMappingRuleGroupValue.value
                                    && mappingRulesGroupValue.value.id === parentMappingRuleGroupValue.value.id)
                            ) {
                                mappingRulesGroupValue.inherited = true;
                            }
                        });
                });

            return this.setMappingRulesGroupInherited(mappingRulesGroup, currentItem.parent);
        }

        return mappingRulesGroup;
    }

    public getMappingRulesGroupValues(
        equalAssignedValues: Map<string, Argument>, attributes: DataAttribute[]
    ): MappingRulesTitleItemValue[] {

        let result = <MappingRulesTitleItemValue[]>[];
        if (equalAssignedValues && equalAssignedValues.keys()) {
            result = [...equalAssignedValues.keys()]
                .map((paramName: string) => {
                    const type = this.getParamOUTreeNodeType(paramName);
                    const friendlyName = this.getParamTitle(paramName, type, attributes);
                    return new MappingRulesTitleItemValue(
                        equalAssignedValues.get(paramName),
                        friendlyName,
                        type
                    );
                });

            result
                .sort((titleItemValueA: MappingRulesTitleItemValue, titleItemValueB: MappingRulesTitleItemValue) => {
                    if (titleItemValueA.type === OUTreeNodeType.ENTITY_TYPE) {
                        return -1;
                    }
                    if (titleItemValueB.type === OUTreeNodeType.ENTITY_TYPE) {
                        return 1;
                    }
                    if (titleItemValueA.type === OUTreeNodeType.SCHOOL) {
                        return -1;
                    }
                    if (titleItemValueB.type === OUTreeNodeType.SCHOOL) {
                        return 1;
                    }
                    if (titleItemValueA.type === OUTreeNodeType.SECTION_VALUE) {
                        return -1;
                    }
                    if (titleItemValueB.type === OUTreeNodeType.SECTION_VALUE) {
                        return 1;
                    }
                    if (titleItemValueA.type === OUTreeNodeType.GROUP_VALUE) {
                        return -1;
                    }
                    if (titleItemValueB.type === OUTreeNodeType.GROUP_VALUE) {
                        return 1;
                    }
                    return (titleItemValueA.title || '').localeCompare(titleItemValueB.title || '');
                });
        }

        return result;
    }

    private getParamOUTreeNodeType(paramName: string): OUTreeNodeType {
        if (paramName === CUSTOM_VALUE_TASK_PARAM_NAME) {
            return OUTreeNodeType.ENTITY_TYPE;
        }
        if (SchoolIdPropertyNames.indexOf(paramName) > -1) {
            return OUTreeNodeType.SCHOOL;
        }
        if (SectionIdPropertyNames.indexOf(paramName) > -1) {
            return OUTreeNodeType.SECTION_VALUE;
        }
        if (GroupIdPropertyNames.indexOf(paramName) > -1) {
            return OUTreeNodeType.GROUP_VALUE;
        }
        return OUTreeNodeType.VALUE;
    }

    private getParamTitle(paramName: string, type: OUTreeNodeType, attributes: DataAttribute[]): string {
        if (type === OUTreeNodeType.ENTITY_TYPE) {
            return 'Entity Type';
        }
        if (type === OUTreeNodeType.SCHOOL) {
            return 'School';
        }
        const attr = attributes.find((item: DataAttribute) => item.propertyName === paramName);
        return (attr && attr.friendlyName) ? attr.friendlyName : paramName;
    }

    private getTaskEduToneAttributes(taskType: Argument, tasks: IntegrationTask[], eduToneDS: string): DataAttribute[] {
        if (taskType) {
            const task = tasks.find((item: IntegrationTask) => item[`${eduToneDS}Type`] === taskType.name);
            if (task) {
                return task[`${eduToneDS}AttributeList`];
            }
        }
        return [];
    }
}


export class ViewTreeNodeItem {
    children?: ViewTreeNodeItem[] = [];
    allChildren?: ViewTreeNodeItem[] = [];
    isExpanded?: boolean;
    title: string;
    hasNestedMappingRules = false;
    isLastEdited = false;
    isMappingRulesListExpanded = false;

    constructor(
        public titles: MappingRulesTitleItemValue[] = [],
        public parent: ViewTreeNodeItem = null,
        public mappingRules: ViewMappingRule[] = [],
        public mappingRulesGroups: MappingRulesGroup[] = [],
        public level: number,
        public isDisabled: boolean = null
    ) {
        this.title = this.getTitle();
        this.hasNestedMappingRules = mappingRules.length > 0;
    }

    public setMappingRulesIsIntermediateNode() {
        this.mappingRules.forEach((mappingRule: ViewMappingRule) => {
            mappingRule.isIntermediateNode = this.doesAnyChildHasMappingRule(mappingRule);
        });
    }

    public doesAnyChildHasMappingRule(mappingRule: MappingRule): boolean {
        return this.children.some((child: ViewTreeNodeItem) =>
            child.mappingRules.find((item: MappingRule) => item.filterIndex === mappingRule.filterIndex) !== undefined
        );
    }

    public getTitle(): string {
        const parentTitle = this.parent ? this.parent.title + ' - ' : '';
        return parentTitle +  this.titles
            .filter((titleItemValue: MappingRulesTitleItemValue) => !!titleItemValue)
            .map((titleItemValue: MappingRulesTitleItemValue) => titleItemValue.value.name)
            .join(', ');
    }

    public addChild(child: ViewTreeNodeItem) {
        child.level = this.level + 1;
        this.children.push(child);

        if (child.hasNestedMappingRules) {
            if (!this.hasNestedMappingRules) {
                this.toggleOnNestedMappingRules();
            }
        }
    }

    public toggleOnNestedMappingRules() {
        this.hasNestedMappingRules = true;

        if (this.parent && !this.parent.hasNestedMappingRules) {
            this.parent.toggleOnNestedMappingRules();
        }
    }
}

export class ViewTreeFlatNodeItem extends ViewTreeNodeItem {
    constructor(
        public titles: MappingRulesTitleItemValue[] = [],
        public parent: ViewTreeFlatNodeItem = null,
        public mappingRules: ViewMappingRule[] = [],
        public mappingRulesGroups: MappingRulesGroup[] = [],
        public level: number,
        public isDisabled: boolean = null,
        public expandable: boolean,
        public children: ViewTreeFlatNodeItem[],
        public isExpanded: boolean,
        public hasNestedMappingRules = false
    ) {
        super(titles, parent, mappingRules, mappingRulesGroups, level, isDisabled);
    }
}


export class MappingRulesGroup {
    constructor(
        public values: MappingRulesTitleItemValue[] = [],
        public isIntermediateNode = true,
        public mappingRules: MappingRule[] = []
    ) { }
}


export class MappingRulesTitleItemValue {
    constructor(
        public value: Argument = null,
        public title = '',
        public type: OUTreeNodeType = null,
        public inherited = false
    ) { }
}


export class MappingRule extends EntityFilterModel {
    public school: OrgItem = null;
    public entityType = '';
    public createNewSchool = false;
    public newSectionsList: Argument[] = null;
    public schoolGuid: string = null;

    static hasPropertyFilter(mappingRule: MappingRule, propertyName: string): boolean {
        return mappingRule.propertyFilters && mappingRule.propertyFilters
            .find((propertyFilter: PropertyFilterModel) => propertyFilter.propertyName === propertyName) !== null;
    }

    static processMappingRule(mappingRules: MappingRule[],
        streamTransformations: MappingRule[],
        task: IntegrationTask,
        dataSourceType: DataSourceType,
        eduToneDS: string): MappingRule[] {
        const orgUnitPathAttributeName = GetOrgUnitPathAttributeName(dataSourceType);
        streamTransformations.forEach((mappingRule: MappingRule) => {
            if (!mappingRule.isSystemFilter) {
                if (mappingRule.customValues) {
                    const customValuesMap = new Map();

                    // ToDo: when backend will give response, check whether customValues has an suitable value
                    for (const propertyName of Object.keys(mappingRule.customValues)) {
                        if (mappingRule.customValues.hasOwnProperty(propertyName) ) {
                            customValuesMap.set(propertyName, mappingRule.customValues[propertyName]);

                            // School Id is set as Custom Value
                            if (FiltersListService.checkIsSchoolIdSelected(propertyName)) {
                                mappingRule.school = new OrgItem(
                                    mappingRule.customValues[propertyName].id,
                                    mappingRule.customValues[propertyName].name
                                );
                            }
                        }
                    }
                    mappingRule.customValues = customValuesMap;
                }

                mappingRule.entityType = task[`${eduToneDS}Type`];
                mappingRule.filterIndex = mappingRules.length;
                ApplicationSettingsService.sortPropertyFiltersByOUPath(mappingRule.propertyFilters, orgUnitPathAttributeName);

                mappingRules.push(mappingRule);
            }
        });

        return mappingRules;
    }
}


export class ViewMappingRule extends MappingRule {
    public isIntermediateNode = true;
}


export class MappingRulesTreeCreator {
    pathParser: OUPathParser;
    mappingRulesOUPathGroupsCreator: MappingRulesOUPathGroupsCreator;
    mappingRulesAssignedValuesGroupsCreator: MappingRulesAssignedValuesGroupsCreator;
    orgUnitPathAttributeName: string;

    constructor(
        public mappingRules: MappingRule[] = [],
        public tasks: IntegrationTask[] = [],
        public eduToneDS = '',
        public edutoneOrgs: OrgItem[] = [],
        public ouList: OrgItem[] = [],
        public sourceOrgTypeMap: Map<string, OrgItemType> = null,
        public dataSourceType: DataSourceType = null,
        public archiveOU: string = ''
    ) {
        this.orgUnitPathAttributeName = GetOrgUnitPathAttributeName(dataSourceType);
        this.pathParser = new OUPathParser(this.dataSourceType);
        this.mappingRulesOUPathGroupsCreator = new MappingRulesOUPathGroupsCreator(
            this.orgUnitPathAttributeName, this.pathParser, this.sourceOrgTypeMap
        );
        this.mappingRulesAssignedValuesGroupsCreator = new MappingRulesAssignedValuesGroupsCreator(this.orgUnitPathAttributeName);
        this.archiveOU = archiveOU;
    }

    setMappingRules(mappingRules: MappingRule[]) {
        this.mappingRules = mappingRules;
    }

    setEdutoneOrgs(edutoneOrgs: OrgItem[]) {
        this.edutoneOrgs = edutoneOrgs;
    }

    createMappingRulesTree(nodeTypes: ViewTreeNodeType[]) {
        return this.createNodeItems(nodeTypes, null, 0);
    }

    private createNodeItems(nodeTypes: ViewTreeNodeType[], parent: ViewTreeNodeItem = null, level: number): ViewTreeNodeItem[] {
        let result: ViewTreeNodeItem[] = [];
        if (nodeTypes.length > 0) {
            const mappingRules: MappingRule[] = (parent ? parent.mappingRules : this.mappingRules || []);

            switch (nodeTypes[0]) {
                case ViewTreeNodeType.SCHOOL: {
                    result = this.createNodeItemsForSchool(mappingRules, nodeTypes, parent, level);
                    break;
                }
                case ViewTreeNodeType.ENTITY_TYPE: {
                    result = this.createNodeItemsForEntityType(mappingRules, nodeTypes, parent, level);
                    break;
                }
                case ViewTreeNodeType.VALUE: {
                    // ToDo: Test if SchoolIdPropertyNames is correct propertyName list
                    // Search mappingRules with not empty customValues (except SchoolId custom value)
                    const customValuesFilter = (key: string) => SchoolIdPropertyNames.indexOf(key) === -1;
                    result = this.createNodeItemsForCustomValue(
                        mappingRules, customValuesFilter, OUTreeNodeType.VALUE, nodeTypes, parent, level);
                    break;
                }
                case ViewTreeNodeType.SECTION: {
                    // ToDo: Test if SectionIdPropertyNames is correct propertyName list
                    const sectionFilter = (key: string) => SectionIdPropertyNames.indexOf(key) > -1;
                    result = this.createNodeItemsForCustomValue(
                        mappingRules, sectionFilter, OUTreeNodeType.SECTION_VALUE, nodeTypes, parent, level);
                    break;
                }
                case ViewTreeNodeType.GROUP: {
                    // ToDo: Test if GroupIdPropertyNames is correct propertyName list
                    const groupFilter = (key: string) => GroupIdPropertyNames.indexOf(key) > -1;
                    result = this.createNodeItemsForCustomValue(mappingRules, groupFilter, OUTreeNodeType.GROUP_VALUE, nodeTypes, parent, level);
                    break;
                }
                case ViewTreeNodeType.OU_TREE: {
                    result = this.createNodeItemsForOUTree(mappingRules, level);
                    break;
                }
                case ViewTreeNodeType.RAW_RULES: {
                    result = this.createNodeItemsForRawRules(mappingRules, level);
                    break;
                }
            }
        }
        return result;
    }

    private createNodeItemsForSchool(
        mappingRules: MappingRule[],
        nodeTypes: ViewTreeNodeType[],
        parent: ViewTreeNodeItem,
        level: number
    ): ViewTreeNodeItem[] {
        // Create list of all edutone schools
        return this.edutoneOrgs.map((org: OrgItem) => {
            const nodeItemMappingRules = mappingRules
                .filter((mappingRule: MappingRule) =>
                    mappingRule.school && (mappingRule.school.identifier === org.identifier) && (mappingRule.school.name === org.name));

            const titleItemValue = new MappingRulesTitleItemValue(
                new Argument(org.identifier, org.name),
                <string>OUTreeNodeType.SCHOOL,
                OUTreeNodeType.SCHOOL
            );

            return this.createNodeItem([titleItemValue], parent, nodeItemMappingRules, nodeTypes.slice(1), level);
        });
    }

    private createNodeItemsForEntityType(
        mappingRules: MappingRule[],
        nodeTypes: ViewTreeNodeType[],
        parent: ViewTreeNodeItem,
        level: number
    ): ViewTreeNodeItem[] {
        // Create list of all entity names (for the case when there are many tasks for one targetType)
        const result: ViewTreeNodeItem[] = [];
        this.getEntityTypesMap()
            .forEach((tasksIdList: string[], entityType: string) => {
                const nodeItemMappingRules = mappingRules
                    .filter((mappingRule: MappingRule) => tasksIdList.indexOf(mappingRule.taskId) > -1);

                const titleItemValue = new MappingRulesTitleItemValue(
                    new Argument(entityType, entityType),
                    <string>OUTreeNodeType.ENTITY_TYPE,
                    OUTreeNodeType.ENTITY_TYPE
                );

                result.push(
                    this.createNodeItem([titleItemValue], parent, nodeItemMappingRules, nodeTypes.slice(1), level)
                );
            });
        return result;
    }

    private createNodeItemsForCustomValue(
        mappingRules: MappingRule[],
        mappingRulesFilter: (string) => boolean,
        nodeType: OUTreeNodeType,
        nodeTypes: ViewTreeNodeType[], // Maybe we will use it later for deeper levels as nodeTypes.slice(1)
        parent: ViewTreeNodeItem,
        level
    ): ViewTreeNodeItem[] {
        return mappingRules
            .filter((mappingRule: MappingRule) =>
                mappingRule.customValues
                && ([...mappingRule.customValues.keys()]
                    .filter((key: string) => mappingRulesFilter)
                ).length > 0
            )
            .map((mappingRule: MappingRule) => {
                const task = this.tasks.find((item: IntegrationTask) => item.taskId === mappingRule.taskId);
                const attributes = task ? task[`${this.eduToneDS}AttributeList`] : [];

                let titles = [];
                if (mappingRule.customValues && mappingRule.customValues.keys()) {
                    titles = [...mappingRule.customValues.keys()]
                        .filter((key: string) => mappingRulesFilter(key))
                        .map((propertyName: string) => {
                            const attr = attributes.find((item: DataAttribute) => item.propertyName === propertyName);
                            return new MappingRulesTitleItemValue(
                                new Argument(propertyName, mappingRule.customValues.get(propertyName).name),
                                (attr && attr.friendlyName) ? attr.friendlyName : propertyName,
                                nodeType
                            );
                        });
                }

                if (titles.length === 0) {
                    return null;
                } else {
                    return this.createNodeItem(titles, parent, [mappingRule], null, level);
                }
            })
            .filter((nodeItem: ViewTreeNodeItem) => !!nodeItem);
    }

    private createNodeItemsForOUTree(availableMappingRules: MappingRule[], level: number): ViewTreeNodeItem[] {
        // Create list of OU Tree
        if (this.ouList) {
            this.ouList.sort((orgItemA: OrgItem, orgItemB: OrgItem) =>
                OrgItem.compare(orgItemA, orgItemB, this.pathParser)
            );
        }

        const result: ViewTreeNodeItem[] = [];
        const availableMappingRulesForGroups = availableMappingRules
            .filter((mappingRule: MappingRule) =>
                mappingRule.propertyFilters.some((propertyFilter: PropertyFilterModel) =>
                    propertyFilter.propertyName === GROUP_MEMBERSHIP
                )
            );

        if (this.ouList) {
            this.ouList
                // ToDo: Do we need to skip root?
                // .filter((org: OrgItem) => org.identifier !== GSUITE_OU_LEVEL_SEPARATOR)
                .forEach((org: OrgItem) => {
                    this.createNodeItemForOUTreeNodeItem(availableMappingRules, availableMappingRulesForGroups, level, org, result, null);
                });
        }

        this.setOUTreeMappingRulesGroups(result, this.tasks, this.eduToneDS);

        return result;
    }

    private createNodeItemForOUTreeNodeItem(
        availableMappingRules: MappingRule[],
        availableMappingRulesForGroups: MappingRule[],
        level: number,
        org: OrgItem,
        tree: ViewTreeNodeItem[],
        parentNode: ViewTreeNodeItem
    ): ViewTreeNodeItem {
        if (parentNode === null) {
            parentNode = this.getParentAndCreateMissingOUTreeNodeItem(org, tree, availableMappingRules, availableMappingRulesForGroups, level);
        }

        const mappingRulesList = (<OUTreeNodeType>org.type === OUTreeNodeType.GROUP)
            ? availableMappingRulesForGroups
            : (parentNode ? parentNode.mappingRules : availableMappingRules);
        const mappingRules = mappingRulesList
            .filter((mappingRule: MappingRule) =>
                this.mappingRulesOUPathGroupsCreator.isMappingRuleContainsPathPart(
                    mappingRule, org.identifier, [this.orgUnitPathAttributeName, GROUP_MEMBERSHIP]
                )
            );

        // Use full DN for top OU (use org.name elsewhere)
        const orgName = (parentNode ? org.name : this.pathParser.getOrgName(org));
        const titleItemValue = new MappingRulesTitleItemValue(
            new Argument(org.identifier, orgName),
            <string>org.type,
            <OUTreeNodeType>org.type
        );

        const currentItem = new ViewTreeNodeItem(
            [titleItemValue], parentNode, <ViewMappingRule[]>mappingRules, [], level);
        // const currentItem = new ViewTreeNodeItem(
        // org.name, org.identifier, ViewTreeNodeType.OU_TREE, parentNode, <ViewMappingRule[]>mappingRules);

        if (parentNode) {
            parentNode.addChild(currentItem);
        } else {
            tree.push(currentItem);
        }

        return currentItem;
    }

    private createNodeItemsForRawRules(availableMappingRules: MappingRule[], level): ViewTreeNodeItem[] {
        // Create list of maping rules
        return availableMappingRules
            .map((mappingRule: MappingRule) => {
                const containerFilters: Argument[] = [];
                const ouFilters = this.mappingRulesOUPathGroupsCreator
                    .getMappingRuleFilterValues(mappingRule, [this.orgUnitPathAttributeName])
                    .filter((arg: Argument) => {
                        const path = arg.id;
                        if (this.sourceOrgTypeMap.get(path) === OrgItemType.CONTAINER) {
                            containerFilters.push(arg);
                            return false;
                        }
                        // (this.sourceOrgTypeMap.get(path) === OrgItemType.OU)
                        return true;
                    });
                const groupFilters = containerFilters.concat(
                    this.mappingRulesOUPathGroupsCreator.getMappingRuleFilterValues(mappingRule, [GROUP_MEMBERSHIP])
                );

                const filters = this.mappingRulesOUPathGroupsCreator.getMappingRulesTitles(ouFilters, groupFilters);

                const task = this.tasks.find((item: IntegrationTask) => item.taskId === mappingRule.taskId);
                const attributes = task ? task[`${this.eduToneDS}AttributeList`] : [];

                const mappingRuleAssignedValues = new Map<string, Argument>(mappingRule.customValues?.entries());
                mappingRuleAssignedValues.set(
                    CUSTOM_VALUE_TASK_PARAM_NAME, new Argument(mappingRule.taskId, mappingRule.entityType));

                const titleItemValues = this.mappingRulesAssignedValuesGroupsCreator
                    .getMappingRulesGroupValues(mappingRuleAssignedValues, attributes);
                const mappingRulesGroup = new MappingRulesGroup(titleItemValues, false, [mappingRule]);

                return new ViewTreeNodeItem(filters, null, <ViewMappingRule[]>[mappingRule], [mappingRulesGroup], level);
            });
    }

    private createNodeItem(
        titles: MappingRulesTitleItemValue[],
        parent: ViewTreeNodeItem,
        mappingRules: MappingRule[],
        childrenNodeTypes: ViewTreeNodeType[] = null,
        level: number
    ): ViewTreeNodeItem {
        const result = new ViewTreeNodeItem(titles, parent, <ViewMappingRule[]>mappingRules, [], level);
        if (childrenNodeTypes && (childrenNodeTypes.length > 0)) {
            result.children = this.createNodeItems(childrenNodeTypes, result, level + 1);
        }
        result.setMappingRulesIsIntermediateNode();
        result.mappingRulesGroups = this.mappingRulesOUPathGroupsCreator.getMappingRulesGroups(result);
        return result;
    }

    private getEntityTypesMap(): Map<string, string[]> {
        const entityTypes: Map<string, string[]> = new Map<string, string[]>();
        this.tasks
            .filter((task: IntegrationTask) => EDUTONE_USER_TASKS_TITLE.includes(task[`${this.eduToneDS}Type`]))
            .forEach((task: IntegrationTask) => {
                if (entityTypes.has(task[`${this.eduToneDS}Type`])) {
                    entityTypes.set(task[`${this.eduToneDS}Type`], [...entityTypes.get(task[`${this.eduToneDS}Type`]), task.taskId]);
                } else {
                    entityTypes.set(task[`${this.eduToneDS}Type`], [task.taskId]);
                }
            });
        return entityTypes;
    }

    private getParentOUTreeNodeItem(ouPath: string, tree: ViewTreeNodeItem[]): ViewTreeNodeItem {
        const ouNodes = this.pathParser.getParentNodeList(ouPath);
        return ouNodes.reduce((parent: ViewTreeNodeItem, ouPart: string) => {
            if (parent === undefined) {
                return parent;
            }
            const nodeList = parent ? parent.children : tree;
            return nodeList.find((node: ViewTreeNodeItem) =>
                (node.titles || [])
                    .find((titleItemValue: MappingRulesTitleItemValue) =>
                        titleItemValue.value.name === ouPart || titleItemValue.value.id === ouPart
                    )
            );
        }, null);
    }

    private getParentAndCreateMissingOUTreeNodeItem(
        org: OrgItem,
        tree: ViewTreeNodeItem[],
        availableMappingRules: MappingRule[],
        availableMappingRulesForGroups: MappingRule[],
        level: number
    ): ViewTreeNodeItem {
        const ouNodes = this.pathParser.getParentNodeList(org.identifier);
        const parentPathPart: string[] = [];
        return ouNodes.reduce((parent: ViewTreeNodeItem, ouPart: string) => {
            if (parent === undefined) {
                return parent;
            }

            parentPathPart.push(ouPart);
            const children = parent ? parent.children : tree;

            let parentNodeItem = children.find((node: ViewTreeNodeItem) =>
                (node.titles || [])
                    .find((titleItemValue: MappingRulesTitleItemValue) =>
                        titleItemValue.value.name === ouPart || titleItemValue.value.id === ouPart
                    )
            );

            if (!parentNodeItem) {
                const missingOrg = new OrgItem(this.pathParser.getPath(parentPathPart), ouPart, org.type);
                if (missingOrg.identifier !== org.identifier) {
                    parentNodeItem = this.createNodeItemForOUTreeNodeItem(availableMappingRules, availableMappingRulesForGroups, level, missingOrg, tree, parent || undefined);
                }
            }

            return parentNodeItem;
        }, null);
    }

    private setOUTreeMappingRulesGroups(tree: ViewTreeNodeItem[], tasks: IntegrationTask[], eduToneDS: string): void {
        tree.forEach((nodeItem: ViewTreeNodeItem) => {
            nodeItem.mappingRulesGroups = this.mappingRulesAssignedValuesGroupsCreator.getMappingRulesGroups(nodeItem, tasks, eduToneDS);

            this.setOUTreeMappingRulesGroups(nodeItem.children, tasks, eduToneDS);
        });
    }
}


export class MappingRulesAutoGenerator {
    orgUnitPathAttributeName: string;
    pathParser: OUPathParser;

    constructor(
        public allMappingRules: MappingRule[] = [],
        public tasks: IntegrationTask[] = [],
        public eduToneDS = '',
        public edutoneOrgs: OrgItem[] = [],
        public ouList: OrgItem[] = [],
        public sourceOrgTypeMap: Map<string, OrgItemType> = null,
        public dataSourceType: DataSourceType = null
    ) {
        this.orgUnitPathAttributeName = GetOrgUnitPathAttributeName(this.dataSourceType);
        this.pathParser = new OUPathParser(this.dataSourceType);
        // ToDo: copy ouList and mappingRules
    }

    setAllMappingRules(mappingRules: MappingRule[]) {
        this.allMappingRules = mappingRules;
    }

    setEdutoneOrgs(edutoneOrgs: OrgItem[]) {
        this.edutoneOrgs = edutoneOrgs;
    }

    generateMappingRules(mappingRules: MappingRule[] = [],
        baseRootPath: string = '',
        entityTypes: string[] = [],
        changedSchools: OrgItemChanges[] = [],
        exclusionList: string[] = [],
        copyUnknownAssignedValues: boolean = false
    ): MappingRule[] {
        if (!entityTypes || (entityTypes.length === 0)) {
            return [];
        }

        let [patterns, prediction]: [MappingRulePattern[], Map<string, [string, NodePatternType, string]>]
            = this.getMappingRulesPatterns(mappingRules, baseRootPath, copyUnknownAssignedValues);
        patterns = this.matchPatterns(patterns, baseRootPath, prediction, exclusionList);
        patterns = this.filterPatternsMatchedPaths(patterns);
        return this.getMappingRulesByMatchedPatterns(patterns, prediction, entityTypes, changedSchools, copyUnknownAssignedValues);
    }

    getMappingRulesPatterns(mappingRules: MappingRule[] = [],
        baseRootPath: string,
        copyUnknownAssignedValues: boolean): [MappingRulePattern[], Map<string, [string, NodePatternType, string]>] {
        const mappingRulesOUPathGroupsCreator = new MappingRulesOUPathGroupsCreator(
            this.orgUnitPathAttributeName, this.pathParser, this.sourceOrgTypeMap
        );
        let prediction = new Map<string, [string, NodePatternType, string]>();
        let patterns: MappingRulePattern[] = [];
        let lastPredictionSize = 0;
        let loopCounter = 0;
        const maxLoopNumber = 100;

        do {
            ++loopCounter;
            patterns = [];
            lastPredictionSize = prediction.size;

            mappingRulesOUPathGroupsCreator
                .getMappingRulesByPathPart(this.allMappingRules, baseRootPath)
                .forEach((mappingRule: MappingRule) => {
                    const mappingRuleOUPathList = [].concat(
                        ...mappingRulesOUPathGroupsCreator.getMappingRulesOUPathList([mappingRule], [this.orgUnitPathAttributeName]),
                    );
                    const mappingRuleGroupPathList = [].concat(
                        ...mappingRulesOUPathGroupsCreator.getMappingRulesOUPathList([mappingRule], [GROUP_MEMBERSHIP])
                    );

                    const assignedValuesMap: Map<string, [NodePatternType, string, string, Argument]> = this.getMappingRuleCustomValues(mappingRule, copyUnknownAssignedValues);
                    const unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>
                        = this.getUnknownAssignedValuesMap(assignedValuesMap);
                    const mappingRulePatternOU = mappingRuleOUPathList
                        .map((ouPath: string) => {
                            const rule: NodePattern[]
                                = this.getPathPattern(ouPath, assignedValuesMap, unknownAssignedValuesMap, prediction);
                            // ToDo: Verify the node pattern rule
                            return new PropertyFilterPattern(rule);
                        });
                    const mappingRulePatternGroup = mappingRuleGroupPathList
                        .map((ouPath: string) => {
                            const rule: NodePattern[]
                                = this.getPathPattern(ouPath, assignedValuesMap, unknownAssignedValuesMap, prediction);
                            // ToDo: Verify the node pattern rule
                            return new PropertyFilterPattern(rule);
                        });

                    // ToDo: Verify the mapping rule pattern
                    patterns.push(
                        new MappingRulePattern(
                            [mappingRule],
                            mappingRulePatternOU,
                            mappingRulePatternGroup,
                            unknownAssignedValuesMap
                        )
                    );
                });

            // ToDo: test
            prediction = this.getPredictionMap(patterns, prediction);
        } while ((prediction.size > lastPredictionSize) && (loopCounter < maxLoopNumber));

        patterns = patterns.filter((pattern: MappingRulePattern) =>
            mappingRules.some((mappingRule: MappingRule) =>
                pattern.sourceMappingRules.some((sourceMappingRule: MappingRule) => sourceMappingRule === mappingRule)
            )
        );

        return [this.validatePatterns(patterns), prediction];
    }

    /**
     * Specify some (const) node patterns based on other mapping rules and get list of assigned values
     */
    getPredictionMap(patterns: MappingRulePattern[],
        prediction: Map<string, [string, NodePatternType, string]> = null): Map<string, [string, NodePatternType, string]> {

        // predicted map: key - value in OU tree, value - [<assigned value>, <assigned value type>, <assigned value property name>]
        if (!prediction) {
            prediction = new Map<string, [string, NodePatternType, string]>();
        }

        // Filter patterns with only 1 "unknown" custom values in source mapping rule
        const patternsToDo = patterns.filter((pattern: MappingRulePattern, index: number) =>
            this.isOnly1UnknownAssignedValues(pattern.unknownAssignedValuesMap)
        );
        const completedPatterns = patterns.filter((pattern: MappingRulePattern, index: number) => pattern.unknownAssignedValuesMap.size === 0);

        patternsToDo.forEach((pattern: MappingRulePattern, index: number) => {
            const [unknownAssignedValue, unknownAssignedValueType, unknownAssignedValuePropertyName]
                = pattern.getLastUnknownAssignedValue();

            // If pattern has 1 "unknown" custom value in source mapping rule,
            // 2 patterns has the same const node (only 1 match),
            // but they have the same "unknown" custom value in source mapping rule - save in predicted list
            // f.e.: /stud/bbb/ou1 & /stud/bbb/ou2 - ou1 & ou2 are defined schools. Unknown custom value - entity type: Students.
            //     /stud == /stud => Students entity
            const samePatternsByUnknownAssignedValues = patternsToDo.filter((itemPattern: MappingRulePattern, itemIndex: number) =>
                itemIndex > index
                && itemPattern.unknownAssignedValuesMap.has(unknownAssignedValue)
                && unknownAssignedValueType === itemPattern.unknownAssignedValuesMap.get(unknownAssignedValue)[0]
                && unknownAssignedValuePropertyName === itemPattern.unknownAssignedValuesMap.get(unknownAssignedValue)[1]
            );

            const constNodePatterns: NodePattern[] = pattern.getConstNodePatterns();
            samePatternsByUnknownAssignedValues.forEach((itemPattern: MappingRulePattern) => {
                const itemPatternConstNodePatterns: NodePattern[] = itemPattern.getConstNodePatterns();

                const equalsPatterns: NodePattern[]
                    = PropertyFilterPattern.getEqualsPatterns(constNodePatterns, itemPatternConstNodePatterns, true);

                if (equalsPatterns && (equalsPatterns.length === 1)) {
                    prediction.set(
                        equalsPatterns[0].value.toLocaleLowerCase(),
                        [unknownAssignedValue, unknownAssignedValueType, unknownAssignedValuePropertyName]
                    );
                }
            });


            // If pattern has 1 "unknown" custom value in source mapping rule,
            // all pattern items are the same, except 1 const diff,
            // and they have 1 diff of "unknown" custom value in source mapping rule - save in predicted list
            // f.e.: /aaa/student/ou1 & /aaa/staff/ou2 - student & staff are defined entity type. Unknown custom value - schools: S1 & S2.
            //     ou1 != ou2 => schools property
            const diffPatternsByUnknownAssignedValues = patternsToDo.filter((itemPattern: MappingRulePattern, itemIndex: number) =>
                itemIndex > index
                && (!itemPattern.unknownAssignedValuesMap.has(unknownAssignedValue)
                || pattern.unknownAssignedValuesMap.get(unknownAssignedValue)[0] !== itemPattern.unknownAssignedValuesMap.get(unknownAssignedValue)[0]
                || pattern.unknownAssignedValuesMap.get(unknownAssignedValue)[1] !== itemPattern.unknownAssignedValuesMap.get(unknownAssignedValue)[1])
            );

            diffPatternsByUnknownAssignedValues.forEach((itemPattern: MappingRulePattern) => {
                const itemPatternConstNodePatterns: NodePattern[] = itemPattern.getConstNodePatterns();

                const diffPatterns: NodePattern[][]
                    = PropertyFilterPattern.getDiffPatterns(constNodePatterns, itemPatternConstNodePatterns, true);
                if (diffPatterns && (diffPatterns.length === 1)) {
                    prediction.set(
                        diffPatterns[0][0].value.toLocaleLowerCase(),
                        [unknownAssignedValue, unknownAssignedValueType, unknownAssignedValuePropertyName]
                    );
                    prediction.set(
                        diffPatterns[0][1].value.toLocaleLowerCase(),
                        itemPattern.getLastUnknownAssignedValue()
                    );
                }
            });


            // If pattern has 1 "unknown" custom value in source mapping rule,
            // and there are only 1 diff on pattern items of completed pattern,
            // set predicted value for missing node pattern - save in predicted list
            // f.e.: /aaa/admins/ou1 & /aaa/students/ou2 - ou1 & ou2 are defined schools, students is entity type.
            // Pattern 1 matched pattern 2 => admins is entity type.
            completedPatterns.forEach((itemPattern: MappingRulePattern) => {
                const diffPatterns: NodePattern[][] = pattern.diff(itemPattern, true);
                if (diffPatterns && (diffPatterns.length >= 1)) {
                    diffPatterns.forEach((diff) => {
                        if ((diff[0].type === NodePatternType.CONST) && (diff[1].type !== NodePatternType.CONST)) {
                            prediction.set(
                                diff[0].value.toLocaleLowerCase(),
                                [unknownAssignedValue, unknownAssignedValueType, unknownAssignedValuePropertyName]
                            );
                        }
                    });
                }
            });
        });

        return prediction;
    }

    isOnly1UnknownAssignedValues(unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>): boolean {
        if (unknownAssignedValuesMap.size < 1) {
            return false;
        }
        const unknownAssignedValuesList: [NodePatternType, string, string, Argument][] = [];
        unknownAssignedValuesMap.forEach(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument], value: string) =>
            unknownAssignedValuesList.push([type, propertyName, entityType, argumentValue])
        );
        return unknownAssignedValuesList.every(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument]) =>
            type === unknownAssignedValuesList[0][0] && propertyName === unknownAssignedValuesList[0][1]
        );
    }

    /**
     * Skip the same or invalid patterns
     */
    validatePatterns(patterns: MappingRulePattern[]): MappingRulePattern[] {
        return patterns.filter((pattern: MappingRulePattern, index: number) => {
            const samePattern = patterns.find((itemPattern: MappingRulePattern, itemIndex: number) =>
                // ToDo: Do we need "similar" patterns? Maybe we should compare only type of node pattern items
                itemIndex > index && pattern.equals(itemPattern, false)
            );

            // ToDo: test
            if (samePattern) {
                samePattern.sourceMappingRules = samePattern.sourceMappingRules.concat(pattern.sourceMappingRules);
                return false;
            } else {
                return pattern.isValid();
            }
        });
    }

    matchPatterns(patterns: MappingRulePattern[],
        baseRootPath: string,
        prediction: Map<string, [string, NodePatternType, string]> = null,
        exclusionList: string[] = []): MappingRulePattern[] {
        // Create list of OU Tree
        if (this.ouList) {
            this.ouList.sort((orgItemA: OrgItem, orgItemB: OrgItem) =>
                OrgItem.compare(orgItemA, orgItemB, this.pathParser)
            );

            this.ouList
                // ToDo: Do we need to skip root?
                // .filter((org: OrgItem) => org.identifier !== GSUITE_OU_LEVEL_SEPARATOR)
                .filter((org: OrgItem) =>
                    (!baseRootPath || this.pathParser.isFirstPathPartMatched(org.identifier, baseRootPath))
                    && (!exclusionList
                    || exclusionList.every((part: string) => !this.pathParser.isPathPartMatched(org.identifier, part)))
                )
                .forEach((org: OrgItem) => {
                    const isOrgItemAvailable = (org.type !== <string>OUTreeNodeType.OU)
                        || this.isOUAvailable(this.allMappingRules, org.identifier);

                    if (isOrgItemAvailable) {
                        const matched = this.findAndSetMatchedPaths(patterns, org, prediction);
                        if (matched === 0) {
                            // this.findAndSetMatchedPaths(patterns, org, prediction, false);
                        }
                    }
                });
        }
        return patterns;
    }

    findAndSetMatchedPaths(patterns: MappingRulePattern[],
        org: OrgItem,
        prediction: Map<string, [string, NodePatternType, string]> = null,
        strict = true): number {
        let matched = 0;
        patterns.forEach((mappingRulePattern: MappingRulePattern) => {
            const mappingRulePatternFilterList: PropertyFilterPattern[] = (org.type === <string>OUTreeNodeType.OU)
                ? mappingRulePattern.ou
                : mappingRulePattern.groups;
            // ToDo: use smth else instead of forEach
            mappingRulePatternFilterList.forEach((propertyFilterPattern: PropertyFilterPattern) => {
                if (propertyFilterPattern.isMatched(org, this.pathParser, prediction, this.tasks, this.eduToneDS, strict)
                    && !propertyFilterPattern.matchedPath.includes(org.identifier)) {

                    propertyFilterPattern.matchedPath.push(org.identifier);
                    ++matched;
                }
            });
        });
        return matched;
    }

    filterPatternsMatchedPaths(patterns: MappingRulePattern[]): MappingRulePattern[] {
        patterns.forEach((mappingRulePattern: MappingRulePattern, index: number) => {
            mappingRulePattern.ou.forEach((propertyFilterPattern: PropertyFilterPattern) => {
                propertyFilterPattern.matchedPath = propertyFilterPattern.matchedPath
                    .filter((ouPath: string) => patterns
                        .find((mappingRulePatternInner: MappingRulePattern, indexInner: number) => mappingRulePatternInner.ou
                            .find((propertyFilterPatternInner: PropertyFilterPattern) => propertyFilterPatternInner.matchedPath
                                .find((ouPathInner: string) => {
                                    const numberOfNodes = this.pathParser.getNumberOfNodes(ouPath);
                                    const numberOfNodesInner = this.pathParser.getNumberOfNodes(ouPathInner);
                                    const numberOfFilterPatterns = propertyFilterPattern.patterns.length;

                                    if (this.pathParser.isFirstPathPartMatched(ouPathInner, ouPath)) {
                                        // false - keep. If any true - reject
                                        if (ouPathInner === ouPath) {
                                            const diffFilters = mappingRulePatternInner.ou.length
                                                                + mappingRulePatternInner.groups.length
                                                                - mappingRulePattern.ou.length
                                                                - mappingRulePattern.groups.length;
                                            if (diffFilters === 0) {
                                                const diffPatterns = propertyFilterPatternInner.patterns.length
                                                                    - propertyFilterPattern.patterns.length;
                                                if (diffPatterns === 0) {
                                                    return index > indexInner;
                                                } else {
                                                    return diffPatterns > 0;
                                                }
                                            } else {
                                                return diffFilters > 0;
                                            }
                                        } else {
                                            const numberOfNodesDiff = (numberOfFilterPatterns - numberOfNodes);
                                            const numberOfNodesDiffInner = (numberOfFilterPatterns - numberOfNodesInner);
                                            return (Math.abs(numberOfNodesDiff) > Math.abs(numberOfNodesDiffInner))
                                                                || ((Math.abs(numberOfNodesDiff) === Math.abs(numberOfNodesDiffInner))
                                                                    && (numberOfNodesDiff < numberOfNodesDiffInner));
                                        }
                                    } else {
                                        if (this.pathParser.isFirstPathPartMatched(ouPath, ouPathInner)) {
                                            const numberOfNodesDiff = (numberOfFilterPatterns - numberOfNodes);
                                            const numberOfNodesDiffInner = (numberOfFilterPatterns - numberOfNodesInner);
                                            return (Math.abs(numberOfNodesDiff) > Math.abs(numberOfNodesDiffInner))
                                                                || ((Math.abs(numberOfNodesDiff) === Math.abs(numberOfNodesDiffInner))
                                                                    && (numberOfNodesDiff < numberOfNodesDiffInner));
                                        } else {
                                            return false;
                                        }
                                    }
                                }))) === undefined);
            });
        });
        return patterns;
    }

    getMappingRulesByMatchedPatterns(patterns: MappingRulePattern[],
        prediction: Map<string, [string, NodePatternType, string]> = null,
        entityTypes: string[] = [],
        changedSchools: OrgItemChanges[] = [],
        copyUnknownAssignedValues: boolean): MappingRule[] {
        let result: MappingRule[] = [];

        const allMappingRulesHashMap = new Map<string, string>();
        this.allMappingRules.forEach((mappingRule: MappingRule) =>
            allMappingRulesHashMap.set(
                EntityFilterModel.getPropertyFiltersHash(mappingRule), EntityFilterModel.getCustomValuesHash(mappingRule)
            )
        );

        patterns.forEach((mappingRulePattern: MappingRulePattern) => {
            let patternMappingRules: MappingRule[] = [];
            mappingRulePattern.ou.forEach((ouPropertyFilterPattern: PropertyFilterPattern) => {
                ouPropertyFilterPattern.matchedPath.forEach((ouMatchedPath: string) => {
                    let mappingRule = this.createMappingRule();
                    mappingRule = this.addPropertyFilter(mappingRule,
                        this.orgUnitPathAttributeName, ouMatchedPath, ouPropertyFilterPattern.patterns, prediction, changedSchools);

                    if (mappingRule) {
                        patternMappingRules.push(mappingRule);
                    }
                });
            });

            mappingRulePattern.groups.forEach((groupPropertyFilterPattern: PropertyFilterPattern) => {
                if (patternMappingRules.length === 0) {
                    patternMappingRules.push(null);
                }

                const mappingRulesByGroups: MappingRule[] = [];
                patternMappingRules.forEach((mappingRule: MappingRule) => {
                    this.getBestSuitedGroups(groupPropertyFilterPattern.matchedPath, mappingRule)
                        .forEach((groupMatchedPath: string) => {
                            let duplicateFilter = null;
                            if (mappingRule) {
                                duplicateFilter = mappingRule.propertyFilters.find((propertyFilter: PropertyFilterModel) =>
                                    (propertyFilter.propertyName === GROUP_MEMBERSHIP)
                                    && propertyFilter.arguments.find((arg: Argument) => arg.id === groupMatchedPath)
                                );
                            }

                            if (!duplicateFilter) {
                                let mappingRuleClone = this.createMappingRule(mappingRule);
                                mappingRuleClone = this.addPropertyFilter(mappingRuleClone,
                                    GROUP_MEMBERSHIP, groupMatchedPath, groupPropertyFilterPattern.patterns, prediction, changedSchools);

                                if (mappingRuleClone) {
                                    mappingRulesByGroups.push(mappingRuleClone);
                                }
                            }
                        });
                });

                patternMappingRules = mappingRulesByGroups;
            });

            patternMappingRules = this.filterInvalidMappingRules(patternMappingRules);
            patternMappingRules = this.filterMappingRules(patternMappingRules, entityTypes, changedSchools, allMappingRulesHashMap);

            if (copyUnknownAssignedValues) {
                patternMappingRules = this.setUnknownAssignedValues(
                    patternMappingRules, mappingRulePattern.unknownAssignedValuesMap, changedSchools
                ).filter((mappingRule: MappingRule) => !!mappingRule);
            }

            // Assign default school for users without school, if it was defined in mapping rules generator dialog
            // Skip Admin entity types?
            patternMappingRules = this.setDefaultSchool(patternMappingRules, changedSchools);

            result = result.concat(patternMappingRules);
        });

        result = this.setFilterIndex(result);

        return result;
    }

    filterInvalidMappingRules(mappingRules: MappingRule[]): MappingRule[] {
        return mappingRules.filter((mappingRule: MappingRule) => mappingRule.taskId);
    }

    filterMappingRules(mappingRules: MappingRule[],
        entityTypes: string[] = [],
        changedSchools: OrgItemChanges[] = [],
        allMappingRulesHashMap = new Map<string, string>()): MappingRule[] {
        return mappingRules.filter((mappingRule: MappingRule) => {
            const mappingRuleHashKey = EntityFilterModel.getPropertyFiltersHash(mappingRule);
            return (!entityTypes || (entityTypes.length === 0) || entityTypes.includes(mappingRule.entityType))
            && !this.isDismissedSchool(mappingRule.school, changedSchools)
            && (!allMappingRulesHashMap.has(mappingRuleHashKey)
                || (allMappingRulesHashMap.get(mappingRuleHashKey) !== EntityFilterModel.getCustomValuesHash(mappingRule)));
        });
    }

    isDismissedSchool(org: OrgItem, changedSchools: OrgItemChanges[] = []): boolean {
        return changedSchools.some((changedSchool: OrgItemChanges) =>
            ((changedSchool.org && org && changedSchool.org.name === org.name)
             || ((changedSchool.org === null) && (org === null)))
            && changedSchool.isDismissed()
        );
    }

    setDefaultSchool(mappingRules: MappingRule[], changedSchools: OrgItemChanges[] = []): MappingRule[] {
        const defaultOrg = changedSchools
            .find((changedSchool: OrgItemChanges) =>
                changedSchool.org === null && !changedSchool.isDisabled && changedSchool.newOrg && !!changedSchool.newOrg.name
            );
        if (defaultOrg) {
            return mappingRules
                .map((mappingRule: MappingRule) => {
                    if (mappingRule.school) {
                        return mappingRule;
                    } else {
                        return this.setMappingRuleSchool(mappingRule, defaultOrg.newOrg.name, changedSchools);
                    }
                });
        }
        return mappingRules.map((mappingRule: MappingRule) => {
            if (mappingRule.school && !mappingRule.school.name) {
                mappingRule.school = null;
                mappingRule.schoolGuid = null;
                if (mappingRule.customValues.has(SCHOOL_ID)) {
                    mappingRule.customValues.delete(SCHOOL_ID);
                }
            }
            return mappingRule;
        });
    }

    setFilterIndex(generatedMappingRules: MappingRule[]): MappingRule[] {
        return generatedMappingRules.map((mappingRule: MappingRule, index: number) => {
            mappingRule.filterIndex = this.allMappingRules.length + index;
            return mappingRule;
        });
    }

    getBestSuitedGroups(groupsMatchedPathList: string[], mappingRule: MappingRule): string[] {
        if (mappingRule && mappingRule.propertyFilters
            && (mappingRule.propertyFilters.length > 0) && (mappingRule.propertyFilters[0].arguments.length > 0)) {

            const nodeList = this.pathParser.getNodeList(mappingRule.propertyFilters[0].arguments[0].id);

            const matches = new Map<string, number>();
            groupsMatchedPathList.forEach((groupMatchedPath: string) => {
                const groupNodeList = this.pathParser.getNodeList(groupMatchedPath);
                let i = 0;
                for (; i < groupNodeList.length && i < nodeList.length && groupNodeList[i] === nodeList[i]; i++) {}
                matches.set(groupMatchedPath, i);
            });

            const maxMatch = Math.max(...matches.values());

            groupsMatchedPathList = groupsMatchedPathList.filter((groupMatchedPath: string) =>
                matches.get(groupMatchedPath) === maxMatch
            );
        }
        return groupsMatchedPathList;
    }

    // this.orgUnitPathAttributeName , GROUP_MEMBERSHIP  , propertyName: string
    createMappingRule(sourceMappingRule: MappingRule = null): MappingRule {
        // ToDo: test
        const mappingRule = new MappingRule(null, FilterAction.STREAM_TRANSFORM);

        if (sourceMappingRule) {
            mappingRule.entityType = sourceMappingRule.entityType;
            mappingRule.taskId = sourceMappingRule.taskId;
            mappingRule.school = sourceMappingRule.school;
            mappingRule.schoolGuid = sourceMappingRule.schoolGuid;

            if (sourceMappingRule.propertyFilters) {
                sourceMappingRule.propertyFilters.forEach((propertyFilter: PropertyFilterModel) => {
                    const newPropertyFilter = new PropertyFilterModel(
                        propertyFilter.propertyName,
                        propertyFilter.predicateName,
                        propertyFilter.args
                    );
                    mappingRule.propertyFilters.push(newPropertyFilter);
                });
            }

            if (sourceMappingRule.customValues) {
                mappingRule.customValues = new Map<string, Argument>(sourceMappingRule.customValues.entries());
            }
        } else {
            mappingRule.customValues = new Map<string, Argument>();
        }
        return mappingRule;
    }

    setMappingRuleSchool(mappingRule: MappingRule, schoolName: string, changedSchools: OrgItemChanges[] = []): MappingRule {
        const changedSchool = changedSchools.find((item: OrgItemChanges) =>
            item.org && (item.org.name.toLowerCase() === schoolName.toLowerCase())
        );
        if (changedSchool) {
            if (changedSchool.newOrg) {
                schoolName = changedSchool.newOrg.name;
            } else {
                return null;
            }
        }

        let school = this.edutoneOrgs.find((org: OrgItem) => org.name.toLowerCase() === schoolName.toLowerCase());
        if (!school) {
            school = new OrgItem(NEW_ORG_IDENTIFIER, schoolName);
            mappingRule.createNewSchool = true;
        }

        if (!mappingRule.school
            || ((mappingRule.school.identifier === school.identifier) && (mappingRule.school.name === school.name))) {
            mappingRule.school = school;
            mappingRule.schoolGuid = mappingRule.school.identifier;
            mappingRule.customValues.set(SCHOOL_ID, new Argument(mappingRule.school.identifier, mappingRule.school.name));
            return mappingRule;
        } else {
            return null;
        }
    }

    setMappingRuleAssignedValue(mappingRule: MappingRule,
        nodePattern: NodePattern,
        value: string,
        prediction: Map<string, [string, NodePatternType, string]> = null,
        changedSchools: OrgItemChanges[] = []): MappingRule {
        if (prediction.has(value.toLocaleLowerCase())) {
            [value] = prediction.get(value.toLocaleLowerCase());
        }
        if (nodePattern.prefix && (value.indexOf(nodePattern.prefix) === 0)) {
            value = value.substr(nodePattern.prefix.length);
        }
        if (nodePattern.suffix && (value.lastIndexOf(nodePattern.suffix) === (value.length - nodePattern.suffix.length))) {
            value = value.substr(0, value.length - nodePattern.suffix.length);
        }

        if (nodePattern.type === NodePatternType.ENTITY_TYPE) {
            const valueLowerCase = value.toLocaleLowerCase();
            mappingRule.entityType = Object.keys(INTEGRATION_TASKS_ORDER).find((eduToneEntity: string) =>
                ((valueLowerCase === eduToneEntity.toLocaleLowerCase()) || (valueLowerCase.indexOf(eduToneEntity.toLocaleLowerCase()) > -1))
                || (!!INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity]
                    && ((INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity].toLocaleLowerCase() === valueLowerCase)
                        || (valueLowerCase.indexOf(INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity].toLocaleLowerCase()) > -1)))
                // || (!!INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[eduToneEntity]
                //     && (INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[eduToneEntity].toLocaleLowerCase() === valueLowerCase))
                || (EDUTONE_TASKS_SYNONYMS.has(eduToneEntity)
                && EDUTONE_TASKS_SYNONYMS.get(eduToneEntity).some((synonymous: string) =>
                    (synonymous === valueLowerCase) || (valueLowerCase.indexOf(synonymous) > -1)
                ))
            );
            // ToDo: use this.tasks['<source/target>Type'] instead of INTEGRATION_TASKS_ORDER

            const task = this.tasks.find((taskItem: IntegrationTask) =>
                taskItem[`${this.eduToneDS}Type`] === mappingRule.entityType
            );
            if (task) {
                if (!mappingRule.taskId || (mappingRule.taskId === task.taskId)) {
                    mappingRule.taskId = task.taskId;
                } else {
                    mappingRule = null;
                }
            }
        } else if (nodePattern.type === NodePatternType.SCHOOL) {
            mappingRule = this.setMappingRuleSchool(mappingRule, value, changedSchools);
        } else if (nodePattern.type === NodePatternType.VALUE) {
            if (mappingRule.customValues.has(nodePattern.propertyName)) {
                if (mappingRule.customValues.get(nodePattern.propertyName).id !== value) {
                    mappingRule = null;
                }
            }
            // else {
            //     mappingRule.customValues.set(nodePattern.propertyName, new Argument(value, value));
            // }
        }
        return mappingRule;
    }

    setUnknownAssignedValues(mappingRules: MappingRule[],
        unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>,
        changedSchools: OrgItemChanges[] = []): MappingRule[] {
        return mappingRules.map((mappingRule: MappingRule) => {
            unknownAssignedValuesMap.forEach(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument], value: string) => {
                if (type === NodePatternType.SCHOOL) {
                    mappingRule = this.setMappingRuleSchool(mappingRule, value, changedSchools);
                } else {
                    if (entityType === mappingRule.entityType) {
                        const task = this.tasks.find((taskItem: IntegrationTask) =>
                            taskItem[`${this.eduToneDS}Type`] === mappingRule.entityType
                        );
                        const attributes = task ? task[`${this.eduToneDS}AttributeList`] : [];
                        const attr = attributes.find((item: DataAttribute) => item.propertyName === propertyName);

                        if (attr) {
                            mappingRule.customValues.set(propertyName, argumentValue);
                        }
                    }
                }
            });
            return mappingRule;
        });
    }

    addPropertyFilter(mappingRule: MappingRule,
        propertyName: string,
        ouPath: string,
        propertyFilterPattern: NodePattern[],
        prediction: Map<string, [string, NodePatternType, string]> = null,
        changedSchools: OrgItemChanges[] = []): MappingRule {
        const isGroup = (propertyName !== this.orgUnitPathAttributeName);
        const predicate = GetMappingRulesPredicate(this.dataSourceType, isGroup);
        const newPropertyFilter = new PropertyFilterModel(
            propertyName,
            predicate,
            [new Argument(ouPath, this.pathParser.getLastNode(ouPath))]
        );
        mappingRule.propertyFilters.push(newPropertyFilter);

        // ToDo: test
        // Set mapping rule custom filter & entity type by propertyFilterPattern
        const nodeList = this.pathParser.getNodeList(ouPath);
        propertyFilterPattern.forEach((nodePattern: NodePattern, index: number) =>  {
            if (mappingRule && (index < nodeList.length)) {
                mappingRule = this.setMappingRuleAssignedValue(mappingRule, nodePattern, nodeList[index], prediction, changedSchools);
            }
        });
        return mappingRule;
    }

    getPathPattern(ouPath: string,
        assignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>,
        unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>,
        prediction: Map<string, [string, NodePatternType, string]> = null): NodePattern[] {
        const result: NodePattern[] = [];
        const nodeList = this.pathParser.getNodeList(ouPath);
        nodeList.forEach((nodeTitle: string) => {
            // ToDo: skip empty nodeTitle
            result.push( this.getNodePatternType(nodeTitle, assignedValuesMap, unknownAssignedValuesMap, prediction) );
        });
        return result;
    }

    getNodePatternType(nodeTitle: string,
        assignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>,
        unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>,
        prediction: Map<string, [string, NodePatternType, string]> = null): NodePattern {
        // Get map of mapping rule custom values
        if (assignedValuesMap.get(nodeTitle.toLocaleLowerCase())) {
            const [type, propertyName] = assignedValuesMap.get(nodeTitle.toLocaleLowerCase());
            unknownAssignedValuesMap = this.deleteUnknownAssignedValueByType(type, unknownAssignedValuesMap);
            return new NodePattern(nodeTitle, type, propertyName);
        }

        let result: NodePattern = null;
        assignedValuesMap.forEach(([type, propertyName]: [NodePatternType, string, string, Argument], value: string) => {
            // f.e. nodeTitle: "Gr. 2", value: "2"

            const pos = nodeTitle.toLocaleLowerCase().indexOf(value);
            if (pos > -1) {
                if (!result || (result.matchedValue.length < value.length)) {
                    // ToDo: limit matched result - use other mapping rules?
                    const prefix = nodeTitle.substr(0, pos);
                    const suffix = nodeTitle.substr(pos + value.length);
                    unknownAssignedValuesMap = this.deleteUnknownAssignedValueByType(type, unknownAssignedValuesMap);
                    result = new NodePattern(nodeTitle, type, propertyName, prefix, suffix, value);
                }
            }
        });

        // ToDo: use invert search const pos = value.indexOf(nodeTitle.toLocaleLowerCase());

        // ToDo: test
        // predicted map: key - value in OU tree, value - [<assigned value>, <assigned value type>, <assigned value property name>]
        if (!result && prediction.has(nodeTitle.toLocaleLowerCase())) {
            const [value, type, propertyName] = prediction.get(nodeTitle.toLocaleLowerCase());
            unknownAssignedValuesMap = this.deleteUnknownAssignedValueByType(type, unknownAssignedValuesMap);
            result = new NodePattern(nodeTitle, type, propertyName, null, null, value);
        }

        return result ? result : new NodePattern(nodeTitle, NodePatternType.CONST);
    }

    getMappingRuleCustomValues(mappingRule: MappingRule, copyUnknownAssignedValues: boolean): Map<string, [NodePatternType, string, string, Argument]> {
        const result: Map<string, [NodePatternType, string, string, Argument]> = new Map<string, [NodePatternType, string, string, Argument]>();

        result.set(mappingRule.entityType.toLocaleLowerCase(), [NodePatternType.ENTITY_TYPE, null, null, null]);
        if (INTEGRATION_TASKS_EDUTONE_TITLE_MAP[mappingRule.entityType]) {
            result.set(INTEGRATION_TASKS_EDUTONE_TITLE_MAP[mappingRule.entityType].toLocaleLowerCase(), [NodePatternType.ENTITY_TYPE, null, null, null]);
        }
        if (INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[mappingRule.entityType]) {
            result.set(INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[mappingRule.entityType].toLocaleLowerCase(), [NodePatternType.ENTITY_TYPE, null, null, null]);
        }
        if (EDUTONE_TASKS_SYNONYMS.has(mappingRule.entityType)) {
            EDUTONE_TASKS_SYNONYMS.get(mappingRule.entityType)
                .forEach((synonymous: string) =>
                    result.set(synonymous, [NodePatternType.ENTITY_TYPE, null, null, null])
                );
        }

        if (mappingRule.school) {
            result.set(mappingRule.school.name.toLocaleLowerCase().trim(),
                [NodePatternType.SCHOOL, mappingRule.school.identifier.toLocaleLowerCase().trim(), null, null]);
        }

        if (copyUnknownAssignedValues) {
            mappingRule.customValues.forEach((value: Argument, propertyName: string) => {
                if (propertyName !== SCHOOL_ID) {
                    // ToDo: implement support of multiple values for groups / sections
                    result.set(value.name.toLocaleLowerCase().trim(), [NodePatternType.VALUE, propertyName, mappingRule.entityType, value]);
                    result.set(value.id.toLocaleLowerCase().trim(), [NodePatternType.VALUE, propertyName, mappingRule.entityType, value]);
                }
            });
        }

        return result;
    }

    getUnknownAssignedValuesMap(assignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>): Map<string, [NodePatternType, string, string, Argument]> {
        const result: Map<string, [NodePatternType, string, string, Argument]> = new Map<string, [NodePatternType, string, string, Argument]>();
        assignedValuesMap.forEach(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument], value: string) => {
            if (!result.has(value)) {
                result.set(value, [type, propertyName, entityType, argumentValue]);
            }
        });
        return result;
    }

    deleteUnknownAssignedValueByType(deleteType: NodePatternType,
        unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>
    ): Map<string, [NodePatternType, string, string, Argument]> {
        unknownAssignedValuesMap.forEach(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument], value: string) => {
            if (type === deleteType) {
                unknownAssignedValuesMap.delete(value);
            }
        });
        return unknownAssignedValuesMap;
    }

    isOUAvailable(mappingRules: MappingRule[], ouPath: string): boolean {
        return mappingRules
            .find((mappingRule: MappingRule) =>
                this.isMappingRuleContainsOUpath(mappingRule, ouPath)
            ) === undefined;
    }

    isMappingRuleContainsOUpath(mappingRule: MappingRule, ouPath: string): boolean {
        return mappingRule.propertyFilters
            .find((propertyFilter: PropertyFilterModel) =>
                // propertyFilter.propertyName === this.orgUnitPathAttributeName &&
                this.doesPropertyFilterContainsOUpath(propertyFilter.arguments, ouPath)
            ) !== undefined;
    }

    doesPropertyFilterContainsOUpath(args: Argument[], ouPath: string): boolean {
        return args.find((arg: Argument) =>
            // ToDo: move toLocaleLowerCase() in initialize() ?
            this.pathParser.isFirstPathPartMatched(arg.id, ouPath)
            || this.pathParser.isFirstPathPartMatched(ouPath, arg.id)
        ) !== undefined;
    }
}


export class MappingRulePattern {
    constructor(
        public sourceMappingRules: MappingRule[],
        public ou: PropertyFilterPattern[] = [],
        public groups: PropertyFilterPattern[] = [],
        public unknownAssignedValuesMap: Map<string, [NodePatternType, string, string, Argument]>
    ) { }

    equals(mappingRulePattern: MappingRulePattern, compareValue = false): boolean {
        if (this.ou.length !== mappingRulePattern.ou.length || this.groups.length !== mappingRulePattern.groups.length) {
            return false;
        }

        const equalsOU = this.ou.every((propertyFilterPattern: PropertyFilterPattern, index: number) =>
            propertyFilterPattern.equals(mappingRulePattern.ou[index], compareValue)
        );
        if (!equalsOU) {
            return false;
        }

        const equalsGroups = this.groups.every((propertyFilterPattern: PropertyFilterPattern, index: number) =>
            propertyFilterPattern.equals(mappingRulePattern.groups[index], compareValue)
        );
        return equalsGroups;
    }

    diff(mappingRulePattern: MappingRulePattern, compareValue = false): NodePattern[][] {
        if (this.ou.length !== mappingRulePattern.ou.length || this.groups.length !== mappingRulePattern.groups.length) {
            return null;
        }

        let diff: NodePattern[][] = [];
        this.ou.forEach((propertyFilterPattern: PropertyFilterPattern, index: number) =>
            diff = diff.concat(propertyFilterPattern.diff(mappingRulePattern.ou[index], compareValue))
        );

        this.groups.forEach((propertyFilterPattern: PropertyFilterPattern, index: number) =>
            diff = diff.concat(propertyFilterPattern.diff(mappingRulePattern.groups[index], compareValue))
        );

        return diff.some((item: NodePattern[]) => item === null) ? null : diff;
    }

    isValid(): boolean {
        // Every Mapping rule should contain Entity Type
        return this.containsNodePatternType(this.ou, NodePatternType.ENTITY_TYPE)
            || this.containsNodePatternType(this.groups, NodePatternType.ENTITY_TYPE);
    }

    getConstNodePatterns(): NodePattern[] {
        return [].concat(
            this.getPatternsByNodePatternType(this.ou, NodePatternType.CONST),
            this.getPatternsByNodePatternType(this.groups, NodePatternType.CONST)
        );
    }

    getLastUnknownAssignedValue(): [string, NodePatternType, string] {
        let unknownAssignedValue = null, unknownAssignedValueType = null, unknownAssignedValuePropertyName = null;
        this.unknownAssignedValuesMap.forEach(([type, propertyName, entityType, argumentValue]: [NodePatternType, string, string, Argument], value: string) => {
            unknownAssignedValue = value;
            unknownAssignedValueType = type;
            unknownAssignedValuePropertyName = propertyName;
        });
        return [unknownAssignedValue, unknownAssignedValueType, unknownAssignedValuePropertyName];
    }

    private containsNodePatternType(propertyFilterPatternList: PropertyFilterPattern[], type: NodePatternType): boolean {
        return propertyFilterPatternList.some((propertyFilterPattern: PropertyFilterPattern) =>
            propertyFilterPattern.patterns.some((nodePattern: NodePattern) =>
                nodePattern.type === type
            )
        );
    }

    private getPatternsByNodePatternType(propertyFilterPatternList: PropertyFilterPattern[], type: NodePatternType): NodePattern[] {
        return [].concat(
            ...propertyFilterPatternList.map((propertyFilterPattern: PropertyFilterPattern) =>
                propertyFilterPattern.patterns.filter((nodePattern: NodePattern) =>
                    nodePattern.type === type
                )
            )
        );
    }
}


export class PropertyFilterPattern {
    constructor(
        public patterns: NodePattern[] = [],
        public matchedPath: string[] = []
    ) { }

    static getEqualsPatterns(patternsA: NodePattern[], patternsB: NodePattern[], compareValue = false): NodePattern[] {
        if (patternsA.length !== patternsB.length) {
            return null;
        }

        return patternsA.filter((patternA: NodePattern, index: number) =>
            patternA.equals(patternsB[index], compareValue)
        );
    }

    static getDiffPatterns(patternsA: NodePattern[], patternsB: NodePattern[], compareValue = false): NodePattern[][] {
        if (patternsA.length !== patternsB.length) {
            return null;
        }

        const diffPatterns: NodePattern[][] = [];
        patternsA.forEach((patternA: NodePattern, index: number) => {
            if (!patternA.equals(patternsB[index], compareValue)) {
                diffPatterns.push([patternA, patternsB[index]]);
            }
        });

        return diffPatterns;
    }

    isMatched(org: OrgItem,
        pathParser: OUPathParser,
        prediction: Map<string, [string, NodePatternType, string]> = null,
        tasks: IntegrationTask[] = [],
        eduToneDS: string,
        strict = true): boolean {
        // ToDo: Use full match for Groups?
        const nodeList = pathParser.getNodeList(org.identifier);
        if (strict && this.patterns.length > nodeList.length) {
            return false;
        }
        if (org.type === OUTreeNodeType.GROUP) {
            if ((this.patterns.length !== nodeList.length)
                || ((this.patterns[this.patterns.length - 1].type === NodePatternType.CONST)
                    && (this.patterns[this.patterns.length - 1].value !== nodeList[nodeList.length - 1]))) {
                return false;
            }
            // const fullMatch = nodeList
            //     .every((nodeTitle: string, index: number) =>
            //         this.pattern[index].value === nodeTitle
            //     );
            // if (fullMatch) {
            //     return false;
            // }
        }
        let isMatched = true;
        let hasAnyNotConstPatternType = false;
        nodeList.forEach((nodeTitle: string, index: number) => {
            if (index < this.patterns.length) {
                isMatched = isMatched && this.patterns[index].isMatched(nodeTitle, prediction, tasks, eduToneDS);
                hasAnyNotConstPatternType = hasAnyNotConstPatternType || (this.patterns[index].type !== NodePatternType.CONST);
            }
        });
        return isMatched && hasAnyNotConstPatternType;
    }

    equals(propertyFilterPattern: PropertyFilterPattern, compareValue = false): boolean {
        const equalPatterns = PropertyFilterPattern.getEqualsPatterns(this.patterns, propertyFilterPattern.patterns, compareValue);
        return equalPatterns ? equalPatterns.length === this.patterns.length : false;
    }

    diff(propertyFilterPattern: PropertyFilterPattern, compareValue = false): NodePattern[][] {
        return PropertyFilterPattern.getDiffPatterns(this.patterns, propertyFilterPattern.patterns, compareValue);
    }
}


export class NodePattern {
    constructor(
        public value: string,
        public type: NodePatternType = NodePatternType.CONST,
        public propertyName: string = null,
        public prefix: string = null,
        public suffix: string = null,
        public matchedValue: string = null
    ) { }

    isMatched(nodeTitle: string,
        prediction: Map<string, [string, NodePatternType, string]> = null,
        tasks: IntegrationTask[] = [],
        eduToneDS: string): boolean {
        // ToDo - use prefix & suffix
        if (this.type === NodePatternType.ENTITY_TYPE) {
            const rawNodeTitle = this.getNodeRawTitle(nodeTitle);
            let predictionValue = prediction.has(nodeTitle.toLocaleLowerCase()) ? prediction.get(nodeTitle.toLocaleLowerCase())[0] : null;
            predictionValue = !predictionValue && prediction.has(rawNodeTitle.toLocaleLowerCase())
                ? prediction.get(rawNodeTitle.toLocaleLowerCase())[0]
                : predictionValue;
            const entity = Object.keys(INTEGRATION_TASKS_ORDER)
                .find((eduToneEntity: string) =>
                    this.isMatchedEduToneEntity(nodeTitle, eduToneEntity)
                    || this.isMatchedEduToneEntity(rawNodeTitle, eduToneEntity)
                    || (predictionValue && this.isMatchedEduToneEntity(predictionValue, eduToneEntity))
                );
            if (entity === undefined) {
                return false;
            }
            return tasks.some((taskItem: IntegrationTask) =>
                taskItem[`${eduToneDS}Type`] === entity
            );
        }
        return true;
    }

    equals(nodePattern: NodePattern, compareValue = false): boolean {
        if (compareValue) {
            return (this.value === nodePattern.value)
                && (this.type === nodePattern.type)
                && (this.propertyName === nodePattern.propertyName);
        } else {
            return (this.type === nodePattern.type)
                && (this.propertyName === nodePattern.propertyName);
        }
    }

    private isMatchedEduToneEntity(nodeTitle: string, eduToneEntity: string): boolean {
        const nodeTitleLowerCase = nodeTitle.toLocaleLowerCase();
        return ((nodeTitleLowerCase === eduToneEntity.toLocaleLowerCase())
            || (nodeTitleLowerCase.indexOf(eduToneEntity.toLocaleLowerCase()) > -1))
            || (!!INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity]
                && ((INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity].toLocaleLowerCase() === nodeTitleLowerCase)
                    || (nodeTitleLowerCase.indexOf(INTEGRATION_TASKS_EDUTONE_TITLE_MAP[eduToneEntity].toLocaleLowerCase()) > -1)))
            // || (!!INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[eduToneEntity]
            //     && (INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP[eduToneEntity].toLocaleLowerCase() === nodeTitleLowerCase))
            || (EDUTONE_TASKS_SYNONYMS.has(eduToneEntity)
                && EDUTONE_TASKS_SYNONYMS.get(eduToneEntity).some((synonymous: string) =>
                    (synonymous === nodeTitleLowerCase) || (nodeTitleLowerCase.indexOf(synonymous) > -1)
                ))
        ;
    }

    private getNodeRawTitle(nodeTitle: string): string {
        return nodeTitle.substring((this.prefix || '').length, nodeTitle.length - (this.suffix || '').length);
    }
}

export class GroupedMappingRules {
    constructor(
        public mappingRules: MappingRule[] = [],
        public school: OrgItem,
        public isDisabled: boolean = false,
        public isExpanded: boolean = false
    ) { }
}

export function GetOrgUnitPathAttributeName(dataSourceType: DataSourceType = null): string {
    if (dataSourceType === DataSourceType.LDAP) {
        return LDAP_ORG_UNIT_PATH;
    } else if (dataSourceType === DataSourceType.GSuite) {
        return GOOGLE_ORG_UNIT_PATH;
    } else {
        return LDAP_ORG_UNIT_PATH;
    }
}

export function GetMappingRulesPredicate(dataSourceType: DataSourceType = null, isGroup: boolean): FilterPredicate {
    if (dataSourceType === DataSourceType.LDAP) {
        return isGroup ? FilterPredicate.ARRAY_ANY_EQUAL : FilterPredicate.ENDS_WITH;
    } else if (dataSourceType === DataSourceType.GSuite) {
        return isGroup ? FilterPredicate.ARRAY_ANY_EQUAL : FilterPredicate.STARTS_WITH;
    } else {
        return isGroup ? FilterPredicate.ARRAY_ANY_EQUAL : FilterPredicate.ENDS_WITH;
    }
}

export enum NodePatternType {
    ENTITY_TYPE = 'entity type',
    SCHOOL = 'school',
    GROUP = 'group',
    SECTION = 'section',
    VALUE = 'value',
    UNKNOWN = 'unknown',
    CONST = 'const'
}

export const EDUTONE_TASKS_SYNONYMS = new Map<string, string[]>([
    ['Staff', ['teacher', 'teachers']]
]);

export const EDUTONE_USER_TASKS_TITLE = ['Student', 'Contact', 'Staff', 'SchoolAdmin'];
export const LDAP_ORG_UNIT_PATH = 'sisIds[ldap_id]';
export const GOOGLE_ORG_UNIT_PATH = 'sisIds[orgUnit]';
export const GROUP_MEMBERSHIP = 'groupMemberships';
export const CUSTOM_VALUE_TASK_PARAM_NAME = '_task';
export const SCHOOL_ID = 'schoolId';

export const SECTIONS_PROPERTY_NAME = 'rosters';
export const SECTIONS_FRIENDLY_NAME = 'Classes';
export const GUID = 'guid';
export const GUID_VALUE = 'guidValue';
export const ROSTERS = 'Rosters';
